interface AuditEvent {
  id: string;
  ts: string;
  agent?: string;
  kind: string;
  summary: string;
  details?: Record<string, string>;
  /** Enforcement verdict, when the event carries one. */
  decision?: "allow" | "ask" | "deny";
  /** false when the verdict was recorded in observe mode (shadow enforcement). */
  enforced?: boolean;
  /** The rule that produced the verdict, e.g. "destructive/rm-rf-root". */
  ruleId?: string;
  /**
   * Marks a row that is itself a security alarm rather than a record of
   * ordinary activity — today only the audit-chain integrity alert. Kept as an
   * explicit flag rather than faking a `decision`, because the row carries no
   * enforcement verdict and printing one would be a lie about what happened.
   */
  critical?: boolean;
  /** A page the row is about, such as a stopped AI session. */
  link?: { href: string; label: string };
}

interface Props {
  event: AuditEvent;
}

export function AuditRow({ event }: Props) {
  return (
    <div className={`sr-audit-row${event.critical ? " is-critical" : ""}`}>
      <div className="sr-audit-row__ts">{event.ts}</div>
      <div className="sr-audit-row__main">
        {event.agent && <span className="sr-audit-row__agent">{event.agent}</span>}
        <span className="sr-audit-row__kind">{event.kind}</span>
        {/* The verdict is the single most important thing about a policy row,
            and the log used to omit it entirely — "policy-decision rm -rf /"
            read identically whether the command ran or was stopped. */}
        {event.decision && (
          <span className={`sr-audit-row__verdict is-${event.decision}`}>
            {event.decision.toUpperCase()}
            {event.enforced === false && " (observed)"}
          </span>
        )}
        <span className="sr-audit-row__summary">{event.summary}</span>
        {event.ruleId && <span className="sr-audit-row__rule">{event.ruleId}</span>}
      </div>
      {event.details &&
        Object.entries(event.details).map(([k, v]) => (
          <div key={k} className="sr-audit-row__detail">
            ↳ {k}: {v}
          </div>
        ))}
      {event.link && (
        <div className="sr-audit-row__detail">
          <a className="sr-link" href={event.link.href}>
            {event.link.label}
          </a>
        </div>
      )}
    </div>
  );
}
